import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { JobsService } from '../../services/jobs.service';
import { TokenService } from '../../services/token.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { MaterialModule } from '../../material/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppService } from '../../services/app.service';
import { CountdownComponent, CountdownModule } from 'ngx-countdown';
import { AnimatedBackgroundComponent } from '../../components/animated-background/animated-background.component';

@Component({
  selector: 'app-register',
  imports: [CommonModule, RouterModule, ReactiveFormsModule, MaterialModule, AnimatedBackgroundComponent, CountdownModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss'
})
export class RegisterComponent {
  private readonly _snackBar = inject(MatSnackBar)
  private readonly _service = inject(AppService)
  private readonly _fb = inject(FormBuilder);
  registerForm: FormGroup = this._fb.group({
    business_name: [null, [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
    confirm_password: [null, [Validators.required]],
    icon: [null]
  });;
  OTPForm: FormGroup = this._fb.group({
    otp: [null, [Validators.required, Validators.maxLength(6), Validators.pattern('[0-9]{6}')]]
  });
  hidePassword = true;
  hideConfirmPassword = true;
  router = inject(Router);
  private _api = inject(JobsService);
  loading = false;
  tokenService = inject(TokenService);
  verifyOTP: boolean = false;
  showTimer = false;
  businessIconPreview: string | null = null;
  private readonly acceptedImageTypes = new Set([
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/svg+xml',
  ]);

  @ViewChild('cd', { static: false }) private countdown!: CountdownComponent;
  private countdownRef: CountdownComponent | undefined;

  constructor() {
    const state: any = this.router.getCurrentNavigation()?.extras.state;
    if (state?.data?.email) {
      this.registerForm.patchValue({ email: state.data.email })
      this.verifyOTP = true;
      setTimeout(() => {
        this.resendOTP();
      }, 200);
    }
  }

  ngAfterViewInit(): void {
    this.countdownRef = this.countdown;
  }

  onSubmit() {
    if (this.loading || this.registerForm.invalid) return;
    if (this.registerForm.value.password != this.registerForm.value.confirm_password) {
      this._snackBar.open("Passwords does not match, Please verify");
      return;
    }

    const payload = {
      ...this.registerForm.value,
      icon: this.registerForm.value.icon || null,
    };

    this.loading = true;
    this._api.register(payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          this.verifyOTP = true;
          this.showTimer = true;
        },
        error: (err: any) => {
          this._snackBar.open(err.error?.message || 'Unable to login. Please try again later.')
        },
      });
  }

  async onBusinessIconSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) return;

    if (!this.acceptedImageTypes.has(file.type)) {
      this._snackBar.open('Please upload a valid image (PNG, JPG, JPEG, WEBP, or SVG).');
      target.value = '';
      return;
    }

    try {
      const dataUrl = await this.fileToDataUrl(file);
      this.businessIconPreview = dataUrl;
      this.registerForm.patchValue({
        icon: this.toRawBase64(dataUrl),
      });
    } catch {
      this._snackBar.open('Unable to process selected image. Please try another file.');
    } finally {
      target.value = '';
    }
  }

  removeBusinessIcon() {
    this.businessIconPreview = null;
    this.registerForm.patchValue({ icon: null });
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to convert file to base64'));
      reader.readAsDataURL(file);
    });
  }

  private toRawBase64(dataUrl: string): string {
    const commaIndex = dataUrl.indexOf(',');
    if (commaIndex === -1) return dataUrl;
    return dataUrl.slice(commaIndex + 1);
  }

  resendOTP() {
    if (this.showTimer) return;
    this.showTimer = true;
    this._api.resendOTP({ email: this.registerForm.value.email }).subscribe({
      next: (res: any) => {
        if (this.countdownRef) {
          this.countdownRef.restart();
          this.showTimer = true;
        }
        this._snackBar.open(res?.message || "OTP sent successfully")
      },
      error: (err: any) => {
        this._snackBar.open(err?.error?.message || 'Unable to send OTP. Please try later.')
      }
    })
  }

  handleCountdown(event: any) {
    if (event.action == 'done') {
      this.showTimer = false;
    }
  }

  onVerify() {
    if (this.loading || this.OTPForm.invalid) return;
    this.loading = true;
    this._api.verifyOTP({
      email: this.registerForm.value.email,
      otp: this.OTPForm.value.otp
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          this._service.setUserProfile({
            access_token: res.data.access_token,
            refresh_token: res.data.refresh_token,
            role: res.data.role
          })
          this.router.navigate(['/jobs']);
        },
        error: (err: any) => this._snackBar.open(err?.error?.message || 'Unable to login. Please try again later.'),
      });
  }
}
